import React from 'react'
import { cn } from '@/utils/cn'
import { useTheme } from '@/hooks/useTheme'

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'bordered' | 'compact' | 'side' | 'glass'
  shadow?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | 'soft' | 'medium' | 'strong'
  hover?: boolean
  theme?: 'auto' | 'light' | 'dark'
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant = 'default', shadow = 'md', hover = false, theme = 'auto', children, ...props }, ref) => {
    const { isDark } = useTheme()

    const baseClasses = cn(
      'rounded-lg',
      theme === 'auto'
        ? (isDark ? 'bg-secondary-900 text-white' : 'bg-white text-secondary-900')
        : ''
    )

    const variantClasses = {
      default: '',
      bordered: cn(
        'border',
        theme === 'auto'
          ? (isDark ? 'border-secondary-600' : 'border-secondary-200')
          : ''
      ),
      compact: 'p-4',
      side: 'flex-row',
      glass: cn(
        'backdrop-blur-md border',
        theme === 'auto'
          ? (isDark
              ? 'bg-secondary-900/80 border-secondary-600/50'
              : 'bg-white/80 border-secondary-200/50')
          : ''
      ),
    }

    const shadowClasses = {
      none: '',
      sm: 'shadow-sm',
      md: 'shadow-md',
      lg: 'shadow-lg',
      xl: 'shadow-xl',
      soft: 'shadow-soft',
      medium: 'shadow-medium',
      strong: 'shadow-strong',
    }

    const themeClasses = {
      auto: '',
      light: 'bg-white text-secondary-900 border-secondary-200',
      dark: 'bg-secondary-800 text-white border-secondary-700',
    }

    const hoverClasses = hover
      ? 'hover:shadow-lg hover:scale-[1.02] transition-all duration-200 cursor-pointer hover:border-primary/20'
      : ''

    return (
      <div
        className={cn(
          baseClasses,
          variantClasses[variant],
          shadowClasses[shadow],
          theme !== 'auto' && themeClasses[theme],
          hoverClasses,
          className
        )}
        ref={ref}
        {...props}
      >
        {children}
      </div>
    )
  }
)

Card.displayName = 'Card'

const CardBody = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const { isDark } = useTheme()
    return (
      <div
        ref={ref}
        className={cn(
          'p-6',
          isDark ? 'text-white' : 'text-secondary-900',
          className
        )}
        {...props}
      />
    )
  }
)

CardBody.displayName = 'CardBody'

const CardTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => {
    const { isDark } = useTheme()
    return (
      <h2
        ref={ref}
        className={cn(
          'text-xl font-semibold font-heading',
          isDark ? 'text-white' : 'text-secondary-900',
          className
        )}
        {...props}
      />
    )
  }
)

CardTitle.displayName = 'CardTitle'

const CardActions = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex justify-end gap-2 mt-4', className)} {...props} />
  )
)

CardActions.displayName = 'CardActions'

// Additional Card components for better theme support
const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('p-6 pb-0', className)} {...props} />
  )
)

CardHeader.displayName = 'CardHeader'

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('p-6 pt-0', className)} {...props} />
  )
)

CardFooter.displayName = 'CardFooter'

export { Card, CardBody, CardTitle, CardActions, CardHeader, CardFooter }